"use client";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { toggleThemeMode, useThemeMode } from "@/hooks/useThemeMode";

/**
 * Sun/moon light–dark switch for the PC + mobile headers. Hidden unless the tenant
 * enabled the feature (`theme.settings.enable_dark_mode`).
 *
 * Both icons are always rendered and CSS shows the right one from `html[data-theme]`,
 * so the server HTML is already correct before hydration — no icon flash, no CLS. The
 * mode is read from the DOM on click (not from render state), so a tap that lands
 * before hydration finishes still toggles the right way.
 */
const ThemeModeToggle: React.FC<{ className?: string }> = ({ className }) => {
  const { data } = useGetCompanyQuery();
  const t = useTranslation();
  const mode = useThemeMode();
  if (!data?.result?.theme?.settings?.enable_dark_mode) return null;

  const label =
    mode === "dark"
      ? t.switch_to_light_mode
      : mode === "light"
        ? t.switch_to_dark_mode
        : t.switch_theme;

  return (
    <button
      type="button"
      className={`theme-mode-toggle${className ? ` ${className}` : ""}`}
      onClick={toggleThemeMode}
      aria-label={label}
      title={label}
    >
      <svg
        className="theme-mode-toggle__moon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
      <svg
        className="theme-mode-toggle__sun"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </svg>
    </button>
  );
};

export default ThemeModeToggle;
